import { z } from 'zod';

/**
 * What should happen to an item (PRD §8.8 "Destination").
 * Two separate fields are used — destination + progressStatus — per the
 * preferred PRD form.
 */
export const DestinationSchema = z.enum([
  'pack', // keep / pack
  'storage', // long-term storage
  'give_away',
  'sell',
  'donate',
  'discard',
  'undecided',
]);
export type Destination = z.infer<typeof DestinationSchema>;

export const DESTINATIONS = DestinationSchema.options;

/** Where the item is in the process (PRD §8.8 "Progress Status"). */
export const ProgressStatusSchema = z.enum([
  'not_started',
  'needs_decision',
  'ready_to_pack',
  'packed',
  'in_storage',
  'completed',
]);
export type ProgressStatus = z.infer<typeof ProgressStatusSchema>;

export const PROGRESS_STATUSES = ProgressStatusSchema.options;

/** Item condition (PRD §8.10). Optional on items. */
export const ConditionSchema = z.enum(['new', 'good', 'used', 'damaged', 'needs_repair']);
export type Condition = z.infer<typeof ConditionSchema>;

export const CONDITIONS = ConditionSchema.options;

/** Dimension unit (PRD §8.9). */
export const DimensionUnitSchema = z.enum(['cm', 'm', 'in', 'ft']);
export type DimensionUnit = z.infer<typeof DimensionUnitSchema>;

export const DIMENSION_UNITS = DimensionUnitSchema.options;

/** Item access/handling priority (PRD §8.5 "priority/access level"). */
export const PrioritySchema = z.enum(['low', 'normal', 'high']);
export type Priority = z.infer<typeof PrioritySchema>;

/**
 * Default room keys (PRD §8.6). These are app-level enum keys translated via
 * i18n — NOT seeded Firestore rows. An item's `room` is either one of these
 * keys or a user-typed custom string.
 */
export const DEFAULT_ROOM_KEYS = [
  'living_room',
  'bedroom',
  'kitchen',
  'bathroom',
  'office',
  'balcony',
  'storage_room',
  'hallway',
  'closet',
  'garage',
  'basement',
  'other',
] as const;
export type DefaultRoomKey = (typeof DEFAULT_ROOM_KEYS)[number];

/**
 * Default category keys (PRD §8.7). Same model as rooms — i18n enum keys,
 * not seeded rows.
 */
export const DEFAULT_CATEGORY_KEYS = [
  'furniture',
  'electronics',
  'kitchenware',
  'clothing',
  'books',
  'decor',
  'documents',
  'appliances',
  'tools',
  'sentimental',
  'outdoor',
  'toys',
  'bedding',
  'cleaning',
  'miscellaneous',
] as const;
export type DefaultCategoryKey = (typeof DEFAULT_CATEGORY_KEYS)[number];

/** Storage location presets (PRD §8.13). Free text is also allowed. */
export const STORAGE_LOCATION_KEYS = [
  'storage_unit',
  'parents_house',
  'friends_apartment',
  'basement',
  'closet',
  'warehouse',
  'other',
] as const;
export type StorageLocationKey = (typeof STORAGE_LOCATION_KEYS)[number];

/** Currencies — ILS-first for the Israeli market. */
export const CURRENCIES = ['ILS', 'USD', 'EUR'] as const;
export type Currency = (typeof CURRENCIES)[number];
